import React, {
  MouseEvent,
  useCallback,
  useState,
  ComponentProps,
  useEffect,
  useRef,
  useImperativeHandle,
} from 'react'
import styled from 'styled-components'

import Input from '../TripModal/TripItems/Common/Fields/RHFInput'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import ListItem from 'components/Luxkit/List/ListItem'

const Container = styled.div`
  position: relative;
`

type ItemElement = HTMLAnchorElement | HTMLButtonElement

export interface BaseAutocompleteProps<T> extends ComponentProps<typeof Input> {
  canRenderDropdown: boolean
  canRenderNoResult: boolean
  isLoading: boolean
  noResultComponent: React.ReactNode
  onClickOutside?: () => void
  onDropdownClose?: () => void
  onOptionSelect: (option: T) => void
  options: T[]
  presentationSelector: (option: T) => ComponentProps<typeof ListItem>
}

function BaseAutocompleteInput<T>(
  {
    canRenderDropdown,
    canRenderNoResult,
    isLoading,
    noResultComponent,
    onClickOutside,
    onDropdownClose,
    onFocus,
    onOptionSelect,
    options,
    presentationSelector,
    helpText,
    ...rest
  }: BaseAutocompleteProps<T>,
  ref: React.Ref<HTMLInputElement>,
) {
  const [isFocused, setIsFocused] = useState(false)
  const [inDropdownContext, setInDropdownContext] = useState(false)

  const handleClickOutside = useCallback(() => {
    if (!inDropdownContext) {
      setIsFocused(false)
    }
  }, [inDropdownContext])

  const handleOnFocus: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setIsFocused(true)
      onFocus?.(e)
    },
    [onFocus],
  )

  const handleItemFocus: React.FocusEventHandler<ItemElement> =
    useCallback(() => {
      setIsFocused(true)
    }, [])

  const onSelect = useCallback(
    (e: MouseEvent<ItemElement>, option: T) => {
      onOptionSelect?.(option)
      setIsFocused(false)
    },
    [onOptionSelect],
  )

  useEffect(() => {
    if (!isFocused) {
      onDropdownClose?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused])

  const triggerRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => triggerRef.current!)

  return (
    <Container
      onMouseEnter={() => setInDropdownContext(true)}
      onMouseLeave={() => setInDropdownContext(false)}
    >
      <Input
        {...rest}
        autoComplete="off"
        onFocus={handleOnFocus}
        ref={triggerRef}
        helpText={helpText}
      />
      <Dropdown
        onClose={handleClickOutside}
        open={isFocused && canRenderDropdown}
        triggerRef={triggerRef}
        autoSize="reference"
      >
        <DropdownContainer data-testid="autocomplete-dropdown">
          {!isLoading &&
            options.map((option, i) => (
              <ListItem
                key={i}
                onClick={(e) => onSelect(e, option)}
                onFocus={handleItemFocus}
                size="medium"
                {...presentationSelector(option)}
              />
            ))}
          {!isLoading && canRenderNoResult && noResultComponent}
          {isLoading && <LoadingIndicator inline />}
        </DropdownContainer>
      </Dropdown>
    </Container>
  )
}

export default React.forwardRef(BaseAutocompleteInput) as <T>(
  props: BaseAutocompleteProps<T> & {
    ref?: React.ForwardedRef<HTMLInputElement>
  },
) => ReturnType<typeof BaseAutocompleteInput>
